import React, { useContext, useEffect, useState } from 'react'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { CompanyTag } from '@/components/ui/badge'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { ViewTabs } from '@/components/ui/tabs'
import {
  AdminPlatformTransferForm,
  AdminPlatformTransferFormValues,
} from '@/routes/Transactions/Details/TransactionDetailsPlatformTransfer/AdminPlatformTransferForm'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { TransactionsStatusReason } from 'api/Transaction/Types/TransactionsStatusReason'
import { TransactionType } from 'api/Wallet/Types/TransactionTypeDto'
import { UpdateWalletTradingAccountTransfer } from 'api/Wallet/Types/UpdateWalletTradingAccountTransfer'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { WarningOutlined } from 'components/Icon'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchTransactionReasonStatusAction,
  fetchTransactionStatusAction,
} from 'store/transactions/transaction.action'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { sleep } from 'utils/sleep'
import { isNotAllowedToChangeState, TransactionStatus } from 'utils/transaction/status.index'
import { getCreditCreditOrFirstOneWallet } from 'utils/wallet'

import ClientAreaAuditLogModal from '../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { TransactionDetailsTab } from '../DetailTabs'
import { LinkedTransactionTable } from '../LinkedTransactions'
import { TransactionDetailsLotsProfitStatistics } from '../TransactionDetailsLotsProfitStatistics/TransactionDetailsLotsProfitStatistics'
import TransactionDetailsNotesPage from '../TransactionDetailsNotes/TransactionDetailsNotesPage'
import TransactionHistoryDetails from '../TransactionHistoryDetails'
import { TransactionsStatisticOverview } from '../TransactionsStatisticOverview/TransactionsStatisticOverview'
import { TransactionStatusHeaderAction } from '../TransactionStatusHeaderAction'
import { TransactionDetailsPlatformTransferGeneralPage } from './TransactionDetailsPlatformTransferGeneralPage'

interface Props {
  data: TransactionDto

  forceRefresh(): void
}

const TransactionDetailsPlatformTransferPage: React.FC<Props> = ({ forceRefresh, data }) => {
  const dispatch = useAppDispatch()

  const [auth] = useContext(AuthSessionContext)
  const setToast = useContext(ToastContext)
  const walletsApiClient = useApiClient(WalletsApiClient)

  const transactionState = useAppSelector((state) => state.transactions.status)
  const statusReasons = useAppSelector((state) => state.transactions.statusReasons)

  const [client, setClient] = useState<{ id: string; name: string } | undefined>(undefined)
  const [transactionWalletId, setTransactionWalletId] = useState<string | undefined>()

  const [tempReason, setTempReason] = useState<TransactionsStatusReason | null>(null)
  const [reasonInput, setReasonInput] = useState<string | null>(null)
  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)
  const [isCustomizeModalVisible, setCustomizeModalVisible] = useState<boolean>(false)

  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false)
  const [confirmationModal, setConfirmationModal] = useState<{
    visible: boolean
    transaction?: TransactionDto
    statusId?: number
    statusReason?: TransactionsStatusReason
  }>({ visible: false })
  const [adminConfirmModal, setAdminConfirmModal] = useState<{
    visible: boolean
    values?: AdminPlatformTransferFormValues
  }>({ visible: false })
  const [activeTab, setActiveTab] = useState<string>(TransactionDetailsTab.GENERAL)

  const permissions = auth?.permissions || []

  useEffect(() => {
    if (data) {
      const transactionWallet = getTransactionWallet(data)
      if (transactionWallet) {
        setClient(transactionWallet.client)
        setTransactionWalletId(transactionWallet.id)
      }
      if (isEmpty(statusReasons)) {
        dispatch(fetchTransactionReasonStatusAction(walletsApiClient))
      }
      if (isEmpty(transactionState)) {
        dispatch(fetchTransactionStatusAction(walletsApiClient))
      }
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: UpdateWalletTradingAccountTransfer, typeId: number) => {
    await walletsApiClient.updateWalletToTradingAccountTransfer(values, typeId)
    setToast(successToast('Transaction updated successfully.'))
    forceRefresh()
  }

  const updateTransactionStatus = async (
    statusId: number,
    statusReason: TransactionsStatusReason | null
  ) => {
    if (data) {
      setConfirmationModal({
        visible: true,
        transaction: data,
        statusId,
        statusReason: statusReason || undefined,
      })
    }
  }
  const confirmAdminSubmit = async (values: AdminPlatformTransferFormValues) => {
    await walletsApiClient.updateDepositWithdrawalMasterTransaction(data.id, {
      transactionStateId: values.transactionStateId.id,
      reason: values.reason,
      walletTransactions: [
        {
          wallet: {
            balanceDelta: values.balanceDelta,
            reservedDelta: values.reservedDelta,
            incomingReservedDelta: values.pendingDelta,
          },
        },
      ],
    })

    setToast(successToast('Transaction updated successfully.'))
    forceRefresh()
  }
  const handleAdminSubmit = async (values: AdminPlatformTransferFormValues) => {
    setAdminConfirmModal({
      visible: true,
      values,
    })
  }
  const handleChangeStatus = async () => {
    const { statusReason, statusId, transaction } = confirmationModal
    if (!statusReason && statusId && transaction) {
      await walletsApiClient.updateTransactionStatus(statusId, { transactionId: transaction.id })
      setToast(successToast('Transaction status updated.'))
    }
    if (statusReason && transaction) {
      if (statusReason.description === 'Other') {
        setTempReason(statusReason)
        setConfirmationModal({ visible: false })
        return setReasonInput(' ')
      } else {
        await walletsApiClient.updateTransactionStatus(statusReason.transactionState.id, {
          transactionId: transaction.id,
          reason: null,
          transactionStateReasonId: statusReason.id,
        })
        setToast(successToast('Transaction status updated.'))
      }
    }
    setConfirmationModal({
      visible: false,
      statusId: undefined,
      transaction: undefined,
      statusReason: undefined,
    })
    forceRefresh()
  }

  const renderMenu = () => {
    if (data && data.state.id === 500) {
      const completed = transactionState.find((x) => x.id === 200)
      if (!completed) {
        return null
      }
      return (
        <DropdownMenuItem onSelect={() => updateTransactionStatus(completed.id, null)}>
          Set without reason
        </DropdownMenuItem>
      )
    }
    return (
      <>
        {transactionState.map((x) => (
          <DropdownMenuSub key={x.id}>
            <DropdownMenuSubTrigger
              disabled={
                !hasPermission(permissions, PermissionsEnum.CanEditTransactionStatus) ||
                x.id === 50 ||
                x.id === 100 ||
                x.id === 600
              }
            >
              {x.name}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onSelect={() => updateTransactionStatus(x.id, null)}>
                Set without reason
              </DropdownMenuItem>
              {statusReasons
                .filter((reason) => reason.transactionState.id === x.id)
                .map((reason) => (
                  <DropdownMenuItem
                    key={reason.id}
                    onSelect={() => updateTransactionStatus(reason.id, reason)}
                    disabled={x.id === 50 || x.id === 100 || x.id === 600}
                  >
                    {reason.description}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </>
    )
  }

  const getTickmillCompanyIfExist = () => {
    return data?.tradingAccountTransactions[0]?.tickmillCompany
  }
  const tickmillCompany = getTickmillCompanyIfExist()

  const isStatusMenuDisabled =
    !hasPermission(permissions, PermissionsEnum.CanEditTransactionStatus) &&
    isNotAllowedToChangeState(data.state.id)

  return (
    <React.Fragment>
      <Dialog open={!!reasonInput && !!tempReason} onOpenChange={() => setReasonInput(null)}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Reason</DialogTitle>
          </DialogHeader>
          <div className='px-4 pb-4'>
            <Field>
              <FieldLabel>Reason</FieldLabel>
              <Input
                onChange={(e) => setReasonInput(e.target.value)}
                type='text'
                value={reasonInput || ''}
              />
            </Field>
            <div className='flex justify-center pt-4'>
              <Button
                type='button'
                onClick={async () => {
                  await walletsApiClient.updateTransactionStatus(tempReason!.transactionState.id, {
                    transactionId: data!.id,
                    reason: reasonInput?.trimLeft(),
                    transactionStateReasonId: tempReason!.id,
                  })
                  setToast(successToast('Transaction status updated.'))
                  await sleep(2000)
                  setReasonInput(null)
                  forceRefresh()
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {showHistoryModal && (
        <Dialog open={showHistoryModal} onOpenChange={() => setShowHistoryModal(false)}>
          <DialogContent className='max-w-100'>
            <TransactionHistoryDetails id={data.id} />
          </DialogContent>
        </Dialog>
      )}
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          transactionId={data.id}
        />
      )}
      {isCustomizeModalVisible && (
        <Dialog open={isCustomizeModalVisible} onOpenChange={() => setCustomizeModalVisible(false)}>
          <DialogContent className='max-w-150'>
            <DialogHeader>
              <DialogTitle>Customize Transaction</DialogTitle>
            </DialogHeader>

            <AdminPlatformTransferForm
              transaction={data}
              onSubmit={handleAdminSubmit}
              onCancel={() => setCustomizeModalVisible(false)}
            />
          </DialogContent>
        </Dialog>
      )}
      {confirmationModal.visible && (
        <ConfirmationModal
          contentText={`Updating a transaction's state while that is ${confirmationModal.transaction?.state.name} could result to issues. Are you sure you want to proceed?`}
          yesButtonText='Yes, proceed'
          onYes={handleChangeStatus}
          onCancel={() => setConfirmationModal({ visible: false })}
          visible={confirmationModal.visible}
        />
      )}
      {adminConfirmModal.visible &&
        adminConfirmModal.values &&
        (() => {
          const v = adminConfirmModal.values
          const fieldPairs: Array<{
            id: string
            label: string
            previousValue: string
            currentValue: string
          }> = [
            {
              id: 'status',
              label: 'Status:',
              previousValue: data.state.name || '',
              currentValue: v.transactionStateId.name,
            },
            {
              id: 'reason',
              label: 'Reason:',
              previousValue: '',
              currentValue: v.reason || '',
            },
            {
              id: 'balanceDelta',
              label: 'Balance Delta:',
              previousValue: '0',
              currentValue: v.balanceDelta.toString(),
            },
            {
              id: 'reservedDelta',
              label: 'Reserved Delta:',
              previousValue: '0',
              currentValue: v.reservedDelta.toString(),
            },
            {
              id: 'pendingDelta',
              label: 'Pending Delta:',
              previousValue: '0',
              currentValue: v.pendingDelta.toString(),
            },
          ]

          const changes = fieldPairs.filter(({ id, previousValue, currentValue }) => {
            const deltaFields = ['balanceDelta', 'reservedDelta', 'pendingDelta']

            if (deltaFields.includes(id)) {
              return previousValue !== currentValue
            }

            return previousValue !== currentValue
          })

          const closeModal = () => {
            setAdminConfirmModal({ visible: false, values: undefined })
            setCustomizeModalVisible(false)
          }

          if (!changes.length) {
            closeModal()
            return null
          }

          return (
            <ConfirmationModal
              renderTitle={
                <>
                  <div>You are going to submit the following changes:</div>
                  <br />
                  <div>
                    {changes.map(({ label, previousValue, currentValue }) => (
                      <div key={label}>
                        <strong>{label}</strong> {previousValue && `${previousValue} → `}
                        {currentValue}
                      </div>
                    ))}
                  </div>
                </>
              }
              yesButtonText='Proceed'
              onYes={() => {
                confirmAdminSubmit(v)
                setAdminConfirmModal({ visible: false, values: undefined })
                setCustomizeModalVisible(false)
              }}
              onCancel={() => setAdminConfirmModal({ visible: false, values: undefined })}
              visible
            />
          )
        })()}
      <Breadcrumbs
        crumbs={[
          {
            title: 'Transaction - Platform',
            link: links.transactionPlatform.url,
          },
          { title: data.referenceId, link: '/transactions/' + data.id },
        ]}
      />
      <NavigationHeader>
        <span className='font-semibold'>{data.referenceId}</span>
        {tickmillCompany && <CompanyTag id={tickmillCompany.id}>{tickmillCompany.name}</CompanyTag>}
        <TransactionStatusHeaderAction
          statusName={data.state.name}
          onStatusClick={() => setShowHistoryModal(true)}
          menuDisabled={isStatusMenuDisabled}
          menuContent={
            data.state.id !== TransactionStatus.Failed && !isNotAllowedToChangeState(data.state.id)
              ? renderMenu()
              : undefined
          }
        />
      </NavigationHeader>

      <ViewTabs
        defaultKey={TransactionDetailsTab.GENERAL}
        activeTab={activeTab}
        onChange={(value) => setActiveTab(value)}
        tabs={[
          {
            key: TransactionDetailsTab.GENERAL,
            translateKey: 'General',
            visible: true,
            component: (
              <TransactionDetailsPlatformTransferGeneralPage
                transaction={data}
                client={client}
                permissions={permissions}
                setAuditLogModal={setAuditLogVisible}
                onSubmit={onSubmit}
                setActiveTab={setActiveTab}
                setCustomizeModal={setCustomizeModalVisible}
              />
            ),
          },
          {
            key: TransactionDetailsTab.STATISTICS,
            translateKey: <span>Trading Account Statistics</span>,
            visible: true,
            component: client && <TransactionsStatisticOverview client={client} />,
          },
          {
            key: TransactionDetailsTab.LOTS_PROFIT_STATS,
            translateKey: <span>Lots/Profit Statistics</span>,
            visible: true,
            component: (
              <TransactionDetailsLotsProfitStatistics
                clientId={client?.id}
                walletId={transactionWalletId}
              />
            ),
          },
          {
            key: TransactionDetailsTab.LINKS,
            translateKey: (
              <span>
                {(!!data.linkedTo.length || !!data.linkedFrom.length) && (
                  <WarningOutlined style={{ color: 'red' }} />
                )}{' '}
                Links
              </span>
            ),
            visible: true,
            component: <LinkedTransactionTable data={data} />,
          },
          {
            key: TransactionDetailsTab.NOTES,
            translateKey: <span>Notes</span>,
            visible: true,
            component: <TransactionDetailsNotesPage transaction={data} />,
          },
        ]}
      />
    </React.Fragment>
  )
}

const getTransactionWallet = (data: TransactionDto) => {
  return data.type.id === TransactionType.NegativeEquityRefund
    ? data.tradingAccountTransactions?.[0]
    : getCreditCreditOrFirstOneWallet(data.walletTransactions)?.wallet
}

export default TransactionDetailsPlatformTransferPage
